import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Wallet, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import CreditFlowLedgerModal from './CreditFlowLedgerModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 7000;
const WHALE_POLL_MS = 45000;   // slower — 45s is plenty for a warning badge
const BASELINE_KEY = 'platform_credits_baseline_v1';
const WHALE_THRESHOLD_KEY = 'platform_whale_threshold_v1';
const CASHOUT_WHALE_THRESHOLD_KEY = 'platform_cashout_whale_threshold_v1';
const DEFAULT_WHALE_THRESHOLD = 500;
const DEFAULT_CASHOUT_WHALE_THRESHOLD = 200;

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSigned = (n) => {
    const abs = Math.abs(Number(n) || 0);
    const sign = (n || 0) >= 0 ? '+' : '-';
    return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtTimeShort = (ts) => {
    if (!ts) return '—';
    try {
        return new Date(ts).toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' });
    } catch { return '—'; }
};
const ctDateStamp = () => {
    try {
        const f = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' });
        return f.format(new Date());
    } catch { return new Date().toISOString().slice(0, 10); }
};

const fmtShort = (n) => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
    return `$${v.toFixed(0)}`;
};

const readBaseline = () => {
    try { return JSON.parse(localStorage.getItem(BASELINE_KEY) || 'null'); } catch { return null; }
};
const writeBaseline = (v) => {
    try { localStorage.setItem(BASELINE_KEY, JSON.stringify(v)); } catch { /* private mode */ }
};
const readWhaleThreshold = () => {
    try {
        const raw = localStorage.getItem(WHALE_THRESHOLD_KEY);
        const n = raw ? Number(raw) : NaN;
        return Number.isFinite(n) && n > 0 ? n : DEFAULT_WHALE_THRESHOLD;
    } catch { return DEFAULT_WHALE_THRESHOLD; }
};
const writeWhaleThreshold = (v) => {
    try { localStorage.setItem(WHALE_THRESHOLD_KEY, String(v)); } catch { /* private mode */ }
};
const readCashoutWhaleThreshold = () => {
    try {
        const raw = localStorage.getItem(CASHOUT_WHALE_THRESHOLD_KEY);
        const n = raw ? Number(raw) : NaN;
        return Number.isFinite(n) && n > 0 ? n : DEFAULT_CASHOUT_WHALE_THRESHOLD;
    } catch { return DEFAULT_CASHOUT_WHALE_THRESHOLD; }
};
const writeCashoutWhaleThreshold = (v) => {
    try { localStorage.setItem(CASHOUT_WHALE_THRESHOLD_KEY, String(v)); } catch { /* private mode */ }
};

/**
 * Live "platform credits" tile. Shows the sum of credits across every
 * player wallet in the caller's scope (admin → platform-wide; cashier in a
 * `is_house` pool → all admin-direct customers; operator-cashier → just
 * their store). Polls every 7 s.
 *
 * Also tracks a per-device shift baseline (snapshotted on first render of
 * each new CT day) and renders a `↑ +$284 since 2:14p` delta next to the
 * main number so the cashier sees at-a-glance whether the float is net-up
 * or net-down for their shift. Right-click the delta to re-anchor.
 */
const PlatformCreditsTile = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [baseline, setBaseline] = useState(readBaseline);
    const [ledgerOpen, setLedgerOpen] = useState(false);
    // Threshold is loaded from the server on mount so it follows the admin
    // across devices. localStorage is kept as an offline fallback while the
    // server responds (avoids a flash of the default $500 badge).
    const [whaleThreshold, setWhaleThreshold] = useState(readWhaleThreshold);
    const [cashoutWhaleThreshold, setCashoutWhaleThreshold] = useState(readCashoutWhaleThreshold);
    const [whales, setWhales] = useState({ whale_count: 0, whales: [] });
    const [cashoutWhales, setCashoutWhales] = useState({ whale_count: 0, whales: [] });
    const lastValueRef = useRef(null);

    // Hydrate the server-side thresholds once. We treat the response as
    // authoritative — if the user changed either on another device, we
    // adopt those values and repaint the badge on the next poll cycle.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/staff/whale-alerts/settings`, {
                    withCredentials: true,
                });
                if (!alive) return;
                const casino = Number(data?.casino_threshold ?? data?.threshold);
                const cashout = Number(data?.cashout_threshold);
                if (Number.isFinite(casino) && casino > 0) {
                    setWhaleThreshold(casino);
                    writeWhaleThreshold(casino);
                }
                if (Number.isFinite(cashout) && cashout > 0) {
                    setCashoutWhaleThreshold(cashout);
                    writeCashoutWhaleThreshold(cashout);
                }
            } catch { /* silent — keep local value */ }
        })();
        return () => { alive = false; };
    }, []);

    // Whale-alert polling. Two independent fetches per cycle — one for
    // casino_play (net-loss ≥ threshold), one for cashouts (gross today
    // ≥ threshold). Combined badge count = both.
    useEffect(() => {
        let alive = true;
        const fetchWhales = async () => {
            try {
                const [casino, cashout] = await Promise.all([
                    axios.get(`${API}/staff/whale-alerts`, {
                        params: { threshold: whaleThreshold, category: 'casino_play' },
                        withCredentials: true,
                    }),
                    axios.get(`${API}/staff/whale-alerts`, {
                        params: { threshold: cashoutWhaleThreshold, category: 'cashouts' },
                        withCredentials: true,
                    }),
                ]);
                if (!alive) return;
                setWhales(casino.data || { whale_count: 0, whales: [] });
                setCashoutWhales(cashout.data || { whale_count: 0, whales: [] });
            } catch {
                // Silent — badge just won't show if the endpoint is down
            }
        };
        fetchWhales();
        const iv = setInterval(fetchWhales, WHALE_POLL_MS);
        return () => { alive = false; clearInterval(iv); };
    }, [whaleThreshold, cashoutWhaleThreshold]);

    const configureThreshold = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const rawCasino = window.prompt(
            'CASINO whale threshold — a player casino net-loss ≥ this many dollars today triggers a badge.',
            String(whaleThreshold),
        );
        if (rawCasino == null) return;
        const nCasino = Number(rawCasino);
        if (!Number.isFinite(nCasino) || nCasino <= 0) return;
        const rawCashout = window.prompt(
            'CASHOUT whale threshold — a player cashing out ≥ this many dollars today triggers a badge.\n\n(Cancel to skip this one and only update casino.)',
            String(cashoutWhaleThreshold),
        );
        let nCashout = null;
        if (rawCashout != null) {
            const v = Number(rawCashout);
            if (Number.isFinite(v) && v > 0) nCashout = v;
        }
        // Optimistic local update
        writeWhaleThreshold(nCasino);
        setWhaleThreshold(nCasino);
        if (nCashout !== null) {
            writeCashoutWhaleThreshold(nCashout);
            setCashoutWhaleThreshold(nCashout);
        }
        try {
            const body = { casino_threshold: nCasino };
            if (nCashout !== null) body.cashout_threshold = nCashout;
            await axios.post(`${API}/staff/whale-alerts/settings`, body, {
                withCredentials: true,
            });
        } catch { /* non-fatal — local values still work */ }
    }, [whaleThreshold, cashoutWhaleThreshold]);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const { data } = await axios.get(`${API}/staff/credits-total`, { withCredentials: true });
                if (!alive) return;
                setData(data); setError(false);
                lastValueRef.current = data.total_credits;
                // Snapshot baseline once per CT day (per device)
                const today = ctDateStamp();
                setBaseline((prev) => {
                    if (!prev || prev.ct_date !== today) {
                        const fresh = { value: data.total_credits, ts: Date.now(), ct_date: today };
                        writeBaseline(fresh);
                        return fresh;
                    }
                    return prev;
                });
            } catch {
                if (alive) setError(true);
            }
        };
        tick();
        const id = setInterval(tick, POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, []);

    const resetBaseline = (e) => {
        e.preventDefault();
        if (lastValueRef.current == null) return;
        const fresh = { value: lastValueRef.current, ts: Date.now(), ct_date: ctDateStamp() };
        writeBaseline(fresh);
        setBaseline(fresh);
    };

    if (error || !data) {
        return (
            <>
                <div
                    className="md:hidden inline-flex items-center justify-center w-8 h-8 rounded-full bg-zinc-800/60 border border-white/10 text-zinc-500"
                    data-testid="platform-credits-tile-loading-mobile"
                >
                    <Wallet className="w-3.5 h-3.5" />
                </div>
                <div
                    className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800/60 border border-white/10 text-zinc-500 text-[11px]"
                    data-testid="platform-credits-tile-loading"
                >
                    <Wallet className="w-3.5 h-3.5" />
                    <span className="font-mono">—</span>
                </div>
            </>
        );
    }

    const delta = baseline ? Number(data.total_credits || 0) - Number(baseline.value || 0) : 0;
    const isFlat = Math.abs(delta) < 0.01;
    const isUp = delta >= 0;
    const since = baseline ? fmtTimeShort(baseline.ts) : '—';
    const ctAnchor = data.ct_anchor_iso ? fmtTimeShort(data.ct_anchor_iso) : '12:00 AM';
    const loadsT = Number(data.loads_today || 0);
    const cashoutsT = Number(data.cashouts_today || 0);
    const netT = Number(data.net_today || 0);
    const oldPaid = Number(data.old_cashouts_paid || 0);
    const owedT = Number(data.owed_today || 0);
    const tooltip = [
        `Total credits across ${data.player_count} player${data.player_count === 1 ? '' : 's'} in ${data.scope} scope.`,
        '',
        `Today (business day since ${ctAnchor} CT):`,
        `  Loads in:                        ${fmt(loadsT)}`,
        `  Cash-outs asked & paid today:   -${fmt(cashoutsT)}`,
        `  Today's profit:                  ${fmtSigned(netT)}`,
        `  Still owed on today's cash-outs: ${fmt(owedT)} (${data.owed_today_count || 0})`,
        '',
        `Old balance paid today (asked before today): ${fmt(oldPaid)} (${data.old_cashouts_count || 0}) — not in today's profit`,
        `Total cash sent today: ${fmt(Number(data.cash_sent_today || 0))}`,
        '',
        `Δ this shift: ${fmtSigned(delta)} since you opened the dashboard at ${since} CT`,
        `Click to open the Credit Flow Ledger — every credit-touching txn categorised.`,
        `(right-click / double-click the Δ pill to re-anchor)`,
    ].join('\n');
    const mobileTooltip = `Float ${fmt(data.total_credits)} · ${fmtSigned(delta)} since ${since} CT\nToday's profit ${fmtSigned(netT)} (loads ${fmt(loadsT)} − today's cash-outs ${fmt(cashoutsT)})\nOld balance paid today ${fmt(oldPaid)} (not in today's profit)\nTap to open the Credit Flow Ledger`;
    const dotColor = isFlat ? 'bg-zinc-500' : isUp ? 'bg-emerald-400' : 'bg-rose-400';

    return (
        <>
            {/* Mobile: compact icon-only pulse-check (wallet + colored dot) */}
            <button
                type="button"
                onClick={() => setLedgerOpen(true)}
                className="md:hidden relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
                title={mobileTooltip + ((whales.whale_count + cashoutWhales.whale_count) > 0
                    ? `\n🚨 ${whales.whale_count + cashoutWhales.whale_count} whale${(whales.whale_count + cashoutWhales.whale_count) === 1 ? '' : 's'} today (casino ≥$${whaleThreshold} or cashout ≥$${cashoutWhaleThreshold})`
                    : '')}
                data-testid="platform-credits-tile-mobile"
            >
                <Wallet className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[10px] font-mono font-black text-emerald-100">{fmtShort(data.total_credits)}</span>
                <span
                    className={`w-2 h-2 rounded-full ${dotColor} ${!isFlat ? 'animate-pulse' : ''}`}
                    data-testid="platform-credits-dot-mobile"
                />
                {(whales.whale_count + cashoutWhales.whale_count) > 0 && (
                    <span
                        className="flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-black animate-pulse"
                        data-testid="whale-alert-badge-mobile"
                    >
                        {whales.whale_count + cashoutWhales.whale_count}
                    </span>
                )}
            </button>

            {/* Desktop: full pill with explicit delta value */}
            <div
                className="hidden md:flex flex-col gap-0.5"
                data-testid="platform-credits-pill-wrapper"
            >
                <button
                    type="button"
                    onClick={() => setLedgerOpen(true)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 transition-colors hover:bg-emerald-500/20 hover:border-emerald-500/50 cursor-pointer"
                    title={tooltip}
                    data-testid="platform-credits-tile"
                >
                    <Wallet className="w-3.5 h-3.5 text-emerald-400" />
                    <div className="flex flex-col leading-tight text-left">
                        <span className="text-[8px] uppercase tracking-widest text-emerald-400/80 font-bold">Player Float</span>
                        <span className="text-[12px] font-mono font-black text-emerald-100">{fmt(data.total_credits)}</span>
                    </div>
                    <div className="flex flex-col leading-tight text-left border-l border-emerald-500/20 pl-2" data-testid="platform-today-profit">
                        <span className="text-[8px] uppercase tracking-widest text-emerald-400/80 font-bold">Today's profit</span>
                        <span className={`text-[12px] font-mono font-black ${netT < 0 ? 'text-rose-300' : 'text-emerald-100'}`} data-testid="platform-today-profit-value">{fmtSigned(netT)}</span>
                    </div>
                    {oldPaid > 0 && (
                        <div className="flex flex-col leading-tight text-left border-l border-emerald-500/20 pl-2" data-testid="platform-old-paid">
                            <span className="text-[8px] uppercase tracking-widest text-amber-300/80 font-bold">Old cash-outs paid</span>
                            <span className="text-[12px] font-mono font-black text-amber-200" data-testid="platform-old-paid-value">{fmt(oldPaid)}</span>
                        </div>
                    )}
                    {baseline && (
                        <span
                            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); resetBaseline(); }}
                            onDoubleClick={(e) => { e.stopPropagation(); resetBaseline(); }}
                            onClick={(e) => e.stopPropagation()}
                            role="button"
                            tabIndex={0}
                            title={`Δ since ${since} CT — right-click / double-click to re-anchor`}
                            className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-mono font-black border transition-colors cursor-context-menu ${
                                isFlat
                                    ? 'bg-zinc-800/60 border-white/10 text-zinc-400'
                                    : isUp
                                        ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30'
                                        : 'bg-rose-500/20 border-rose-500/40 text-rose-300 hover:bg-rose-500/30'
                            }`}
                            data-testid="platform-credits-delta"
                        >
                            {isFlat ? (
                                <span>flat</span>
                            ) : isUp ? (
                                <><ArrowUp className="w-2.5 h-2.5" /><span>{fmtSigned(delta)}</span></>
                            ) : (
                                <><ArrowDown className="w-2.5 h-2.5" /><span>{fmtSigned(delta)}</span></>
                            )}
                        </span>
                    )}
                    {(whales.whale_count + cashoutWhales.whale_count) > 0 && (
                        <span
                            onContextMenu={configureThreshold}
                            onClick={(e) => e.stopPropagation()}
                            role="button"
                            tabIndex={0}
                            title={
                                (() => {
                                    const parts = [];
                                    if (whales.whale_count > 0) {
                                        parts.push(`${whales.whale_count} casino whale${whales.whale_count === 1 ? '' : 's'} today (net-loss ≥ $${whaleThreshold})`);
                                    }
                                    if (cashoutWhales.whale_count > 0) {
                                        parts.push(`${cashoutWhales.whale_count} cashout whale${cashoutWhales.whale_count === 1 ? '' : 's'} today (cashed out ≥ $${cashoutWhaleThreshold})`);
                                    }
                                    return parts.join('\n') + '\n\nClick tile to see all. Right-click badge to change thresholds.';
                                })()
                            }
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-black border cursor-context-menu bg-rose-500/25 border-rose-500/60 text-rose-100 animate-pulse hover:bg-rose-500/40"
                            data-testid="whale-alert-badge"
                        >
                            <AlertTriangle className="w-2.5 h-2.5" />
                            <span>{whales.whale_count + cashoutWhales.whale_count}</span>
                        </span>
                    )}
                </button>
                {/* CT-anchored breakdown — sits directly below the pill so staff
                    can read the net delta + how it decomposes at a glance.
                    Hidden when there's truly zero activity today (keeps the
                    header clean for empty mornings). */}
                {(loadsT > 0 || cashoutsT > 0) && (
                    <div
                        className="flex items-center gap-1.5 text-[10px] font-mono text-emerald-200/70 px-3"
                        data-testid="platform-credits-breakdown"
                        title={`Since ${ctAnchor} CT today`}
                    >
                        <span>since {ctAnchor} CT</span>
                        <span className="text-emerald-300/60">·</span>
                        <span className="text-emerald-300" data-testid="platform-credits-loads-today">+{fmt(loadsT)} in</span>
                        <span className="text-emerald-300/60">·</span>
                        <span className="text-rose-300" data-testid="platform-credits-cashouts-today">-{fmt(cashoutsT)} out</span>
                    </div>
                )}
            </div>

            <CreditFlowLedgerModal
                open={ledgerOpen}
                onClose={() => setLedgerOpen(false)}
                whales={whales.whales || []}
                whaleThreshold={whaleThreshold}
                cashoutWhales={cashoutWhales.whales || []}
                cashoutWhaleThreshold={cashoutWhaleThreshold}
                onConfigureThreshold={configureThreshold}
            />
        </>
    );
};

export default PlatformCreditsTile;
